import { Button } from 'antd';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { PrinterOutlined } from '@ant-design/icons';
import { MedicineSection } from '../indor-casepaper/components/MedicineSection';
import { Formik } from 'formik';
import { useSelector } from 'react-redux';
import { medicinesValidationSchema } from '../indor-casepaper/validation/schema';
import { openPrintPreview } from '../../components/Print';
import { transformMedicinesFromAPI } from '../indor-casepaper/utils/dataTransformers';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useFormSubmission, useServiceDetails } from '../indor-casepaper/hooks';
import { medicineInit } from '../indor-casepaper/constants';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { GetMedicalDetailAction } from '../../store/Actions/PatientAction';
import ConsultationHistory from './components/ConsultationHistory';
import { Breadcrumb } from '../../components/Card/Cards';
import PatientCCPaperView from '../../components/PatientCCPaperView/PatientCCPaperView';
import { ActionGetPatientPlanList } from '../../store/Actions/AppConfigAction';
import { calculateAge } from '../../utils/functions';

const Medications = () => {
    const { loader, patientPlanList } = useSelector(state => state?.appConfig || {});
    const todayDate = moment().format('YYYY-MM-DD');
    const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
    const { patientPlanId, id } = useParams();
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;
    const consultationId = searchParams.get('consultationId');
    const roomName = searchParams.get('roomName');
    const roomType = searchParams.get('roomType');

    const { medicineList, MedicalDetail, CcPaperDetail } = useSelector(state => state?.patient);
    const dispatch = useDispatch();
    const [error, setError] = useState(null);
    const { patientPlanData, currentIndex, processObjectsInPairs, handlePlanChange } =
        useServiceDetails(patientPlanList, selectedPlanIndex);
    const { handleSubmitMedicines: onFormSubmit } = useFormSubmission(
        patientPlanId,
        null,
        setError,
        patientPlanList
    );
    const treatmentName = patientPlanList?.treatmentName;
    const birthDate = CcPaperDetail?.birthDate;
    const age = birthDate ? calculateAge(birthDate) : CcPaperDetail?.age;
    const gender = CcPaperDetail?.gender;
    const phoneNo = CcPaperDetail?.phoneNo;
    const email = CcPaperDetail?.email;

    const handlePrescriptionPrint = values => {
        const customerData = {
            name: CcPaperDetail?.name,
            email: CcPaperDetail?.email,
            age: CcPaperDetail?.age,
            phoneNo: CcPaperDetail?.phoneNo,
            gender: CcPaperDetail?.gender,
            birthDate: CcPaperDetail?.birthdate,
            registrationNo: CcPaperDetail?.registrationNo,
        };
        // Use the first clinical history if available, otherwise patient detail or customer data
        const displayData = (MedicalDetail && MedicalDetail[0]) || CcPaperDetail || customerData;

        // 1. Patient Details
        const patientSection = {
            title: 'Patient Details',
            fields: [
                { label: 'Registration No', value: customerData?.registrationNo || '-' },
                { label: 'Name', value: customerData?.name || '-' },
                { label: 'Treatment Name', value: treatmentName || '-' },
                {
                    label: 'Age/Gender',
                    value: `${age || displayData?.age || '-'}/${gender || displayData?.gender || '-'}`,
                },
                { label: 'Phone No', value: phoneNo || displayData?.phoneNo || '-' },
                { label: 'Email', value: email || displayData?.email || '-' },
                { label: 'Room Name', value: roomName || '-' },
                { label: 'Room Type', value: roomType || '-' },
            ],
        };

        // 2. Prescribed Medicines (Table Format)
        const medicines = values?.medicines || [];
        const medicineTableSection = {
            title: 'Prescribed Medicines',
            type: 'table',
            headers: ['Sr.', 'Medicine Name', 'Dosage', 'Timing', 'Course', 'Anupan', 'Date'],
            data: medicines.map((med, index) => [
                index + 1,
                med.medicineName || '-',
                med.howMuch || '-',
                Array.isArray(med.whenToTake) ? med.whenToTake.join(', ') : med.whenToTake || '-',
                `${med.course || '-'} (${med.days || '-'} days)`,
                med.anupan || '-',
                med.date || '-',
            ]),
        };

        openPrintPreview(displayData, 'Prescription Report', {
            sections: [patientSection, medicineTableSection],
            title: `Prescription: ${displayData?.name || ''}`,
        });
    };

    const initialMedicines = useMemo(() => {
        // UPDATED: Check if it's an object with keys instead of .length
        if (patientPlanList?.medicine && Object.keys(patientPlanList.medicine).length > 0) {
            const transformed = transformMedicinesFromAPI(patientPlanList.medicine);
            return transformed;
        }
        return [{ ...medicineInit, date: todayDate }];
    }, [patientPlanList?.medicine, todayDate]);
    useEffect(() => {
        if (consultationId) {
            dispatch(GetMedicalDetailAction({ Query: `/${consultationId}` }));
        }
        if (patientPlanList?.length === 0) {
            dispatch(ActionGetPatientPlanList({ Query: `/${patientPlanId}` }));
        }
    }, [consultationId, dispatch]);

    const BreadcrumbList = [
        {
            name: 'Dashboard',
            // linkTo: `/order-history`,
        },
        {
            name: 'Indoor CasePaper',
        },
        {
            name: 'Prescribed Medicines',
        },
    ];
    return (
        <div className="main-wrapper">
            <Header />
            <Sidebar />
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <Breadcrumb page={BreadcrumbList} />
                    {/* </div> */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <Formik
                                        enableReinitialize
                                        initialValues={{
                                            medicines: initialMedicines,
                                        }}
                                        validationSchema={medicinesValidationSchema}
                                        onSubmit={onFormSubmit}
                                    >
                                        {formikProps => (
                                            <>
                                                <div className="col-12 d-flex justify-content-between align-items-center mt-5 mb-2 px-3">
                                                    <h4 className="mb-0" style={{ fontWeight: '600', color: '#fa8c16' }}>
                                                        Prescribed Medicines
                                                    </h4>
                                                    <Button
                                                        icon={<PrinterOutlined />}
                                                        onClick={() => handlePrescriptionPrint(formikProps.values)}
                                                        style={{ borderRadius: '8px' }}
                                                    >
                                                        Print Prescription
                                                    </Button>
                                                </div>
                                                <PatientCCPaperView
                                                    userId={id}
                                                    extraInfo={{ 'Room Name': roomName, 'Room Type': roomType }}
                                                />

                                                {/* Previous Consultation History */}
                                                {MedicalDetail &&
                                                    Array.isArray(MedicalDetail) &&
                                                    MedicalDetail.length > 0 && (
                                                        <ConsultationHistory consultationData={MedicalDetail} />
                                                    )}

                                                <MedicineSection
                                                    formikProps={formikProps}
                                                    todayDate={todayDate}
                                                    patientPlanData={patientPlanData}
                                                />
                                            </>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Medications;
